import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/ui/modal";
import Input from "@/components/ui/input";
import Button from "@/components/ui/button";
import FileInput from "@/components/ui/file-input";
import { Plus as PlusIcon } from "@/components/icons";
import LibraryShareCustomFields from "../LibraryShareCustomFields";
import "./index.scss";

export type ShareOption = "everyone" | "admins" | "custom";

/** "create" = Yeni Klasör Oluştur (no file input), "add" = Klasör Ekle (with file input) */
export type AddFolderModalMode = "create" | "add";

export interface AddFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** When "create", only folder name + share. When "add", includes file upload. */
  mode?: AddFolderModalMode;
  onSubmit?: (payload: {
    folderName: string;
    share: ShareOption;
    sharedUserIds?: string[];
    sharedDepartmentIds?: string[];
    /** Önceden yüklenmiş dosya id'leri (seçimde hemen yükleme kullanıldığında) */
    uploadedFileIds?: string[];
    /** Eski akış: butona basınca yüklenecek dosyalar (uploadedFileIds yoksa) */
    files?: File[];
  }) => void | Promise<void>;
}

const AddFolderModal: React.FC<AddFolderModalProps> = ({
  isOpen,
  onClose,
  mode = "create",
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [folderName, setFolderName] = useState("");
  const [share, setShare] = useState<ShareOption>("everyone");
  const [sharedUserIds, setSharedUserIds] = useState<string[]>([]);
  const [sharedDepartmentIds, setSharedDepartmentIds] = useState<string[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  /** Seçimde hemen yükleme: dosyalar seçilir seçilmez yüklenir, id'ler burada toplanır */
  const [uploadedFileIds, setUploadedFileIds] = useState<string[]>([]);

  const resetForm = () => {
    setFolderName("");
    setShare("everyone");
    setSharedUserIds([]);
    setSharedDepartmentIds([]);
    setSelectedFiles([]);
    setUploadedFileIds([]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleFileSelect = (files: File[]) => {
    setSelectedFiles(files);
    setUploadedFileIds([]);
  };

  const handleFileRemove = () => {
    setSelectedFiles([]);
    setUploadedFileIds([]);
  };

  const handleUploadComplete = () => {};

  const handleSubmit = async () => {
    if (!folderName.trim()) return;
    try {
      await onSubmit?.({
        folderName: folderName.trim(),
        share,
        sharedUserIds:
          share === "custom" && sharedUserIds.length > 0
            ? sharedUserIds
            : undefined,
        sharedDepartmentIds:
          share === "custom" && sharedDepartmentIds.length > 0
            ? sharedDepartmentIds
            : undefined,
        uploadedFileIds:
          uploadedFileIds.length > 0 ? uploadedFileIds : undefined,
        files:
          uploadedFileIds.length === 0 && selectedFiles.length > 0
            ? selectedFiles
            : undefined,
      });
      handleClose();
    } catch {
      // Hata toast'ı sayfada veya apiClient'da gösteriliyor; modal açık kalır
    }
  };

  const isFormValid =
    folderName.trim().length > 0 &&
    (mode !== "add" ||
      (selectedFiles.length > 0 &&
        uploadedFileIds.length === selectedFiles.length));

  const customHeader = (
    <div className="add-folder-modal-header">
      <div className="add-folder-modal-header-left">
        <span className="add-folder-modal-header-icon">
          <PlusIcon width={20} height={20} color="#14181f" />
        </span>
        <h3>
          {mode === "add"
            ? t("library.addFolderModal.titleAdd")
            : t("library.addFolderModal.title")}
        </h3>
      </div>
      <button
        type="button"
        className="add-folder-modal-close"
        onClick={handleClose}
        aria-label={t("common.close")}
      >
        ✕
      </button>
    </div>
  );

  const customFooter = (
    <div className="add-folder-modal-footer">
      <Button variant="text" color="secondary" onClick={handleClose}>
        {t("library.addFolderModal.cancel")}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        disabled={!isFormValid}
      >
        {t("library.addFolderModal.create")}
      </Button>
    </div>
  );

  return (
    <Modal
      title={
        mode === "add"
          ? t("library.addFolderModal.titleAdd")
          : t("library.addFolderModal.title")
      }
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      customHeader={customHeader}
      customFooter={customFooter}
    >
      <div className="add-folder-modal-content">
        <div className="add-folder-modal-field">
          <Input
            label={t("library.addFolderModal.folderName")}
            placeholder={t("library.addFolderModal.folderNamePlaceholder")}
            value={folderName}
            maxLength={255}
            onChange={(e) => setFolderName(e.target.value)}
          />
        </div>

        {mode === "add" && (
          <div className="add-folder-modal-field">
            <FileInput
              label={t("library.addFolderModal.uploadFolder")}
              placeholder={t("library.addFolderModal.dropzoneText")}
              supportedFormats={["PDF", "DOC", "DOCX", "XLS", "XLSX"]}
              maxSize={500}
              maxFiles={500}
              allowFolder
              onFolderName={setFolderName}
              onFileSelect={handleFileSelect}
              onFileRemove={handleFileRemove}
              onUploadComplete={handleUploadComplete}
              uploadToBackend
              onUploadSuccess={(fileId) =>
                setUploadedFileIds((prev) => [...prev, fileId])
              }
              enableDemoProgress={false}
              fileType="file"
              buttonLabel={t("library.addFolderModal.addFolderButton")}
            />
          </div>
        )}

        <div className="add-folder-modal-share">
          <span className="add-folder-modal-share-title">
            {t("library.addFolderModal.share")}
          </span>
          <div className="add-folder-modal-share-options">
            <label
              className={`add-folder-modal-radio-option ${share === "everyone" ? "selected" : ""}`}
            >
              <input
                type="radio"
                name="share"
                checked={share === "everyone"}
                onChange={() => setShare("everyone")}
              />
              <span className="add-folder-modal-radio-text">
                <span className="add-folder-modal-radio-label">
                  {t("library.addFolderModal.everyone")}
                </span>
                <span className="add-folder-modal-radio-desc">
                  {t("library.addFolderModal.everyoneDesc")}
                </span>
              </span>
            </label>
            <label
              className={`add-folder-modal-radio-option ${share === "admins" ? "selected" : ""}`}
            >
              <input
                type="radio"
                name="share"
                checked={share === "admins"}
                onChange={() => setShare("admins")}
              />
              <span className="add-folder-modal-radio-text">
                <span className="add-folder-modal-radio-label">
                  {t("library.addFolderModal.admins")}
                </span>
                <span className="add-folder-modal-radio-desc">
                  {t("library.addFolderModal.adminsDesc")}
                </span>
              </span>
            </label>
            <label
              className={`add-folder-modal-radio-option ${share === "custom" ? "selected" : ""}`}
            >
              <input
                type="radio"
                name="share"
                checked={share === "custom"}
                onChange={() => setShare("custom")}
              />
              <span className="add-folder-modal-radio-text">
                <span className="add-folder-modal-radio-label">
                  {t("library.addFolderModal.custom")}
                </span>
                <span className="add-folder-modal-radio-desc">
                  {t("library.addFolderModal.customDesc")}
                </span>
              </span>
            </label>
          </div>
          {share === "custom" && (
            <LibraryShareCustomFields
              sharedUserIds={sharedUserIds}
              sharedDepartmentIds={sharedDepartmentIds}
              onUsersChange={setSharedUserIds}
              onDepartmentsChange={setSharedDepartmentIds}
              isVisible={isOpen}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AddFolderModal;
